import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Close,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Error as ErrorIcon,
  CloudUpload,
  Cancel,
  CleaningServices,
} from '@mui/icons-material';
import { formatFileSize } from '../../utils/formatters';

function StatusIcon({ status }) {
  switch (status) {
    case 'done':
      return <CheckCircle color="success" fontSize="small" />;
    case 'error':
      return <ErrorIcon color="error" fontSize="small" />;
    case 'uploading':
      return <CloudUpload color="primary" fontSize="small" />;
    default:
      return <CloudUpload color="disabled" fontSize="small" />;
  }
}

export default function UploadQueue({
  items,
  hasActive,
  globalProgress,
  onCancelItem,
  onClearCompleted,
  onClearAll,
}) {
  const [expanded, setExpanded] = useState(true);

  if (items.length === 0) return null;

  const doneCount = items.filter((i) => i.status === 'done').length;
  const errorCount = items.filter((i) => i.status === 'error').length;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: 380,
        maxHeight: '60vh',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <CloudUpload sx={{ mr: 1 }} fontSize="small" />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {hasActive
            ? `Upload in corso... ${globalProgress}%`
            : `Upload completato (${doneCount}/${items.length})`}
        </Typography>
        {!hasActive && (
          <Tooltip title="Pulisci lista">
            <IconButton
              size="small"
              sx={{ color: 'white', mr: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                onClearCompleted();
              }}
            >
              <CleaningServices fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          size="small"
          sx={{ color: 'white', mr: 0.5 }}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
        </IconButton>
        <IconButton
          size="small"
          sx={{ color: 'white' }}
          onClick={(e) => {
            e.stopPropagation();
            onClearAll();
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Progress bar */}
      {hasActive && (
        <LinearProgress
          variant="determinate"
          value={globalProgress}
          sx={{ height: 3 }}
        />
      )}

      {/* File list */}
      <Collapse in={expanded}>
        <List dense sx={{ maxHeight: 300, overflow: 'auto', py: 0 }}>
          {items.map((item) => (
            <ListItem
              key={item.id}
              secondaryAction={
                (item.status === 'pending' || item.status === 'uploading') && (
                  <IconButton size="small" onClick={() => onCancelItem(item.id)}>
                    <Cancel fontSize="small" />
                  </IconButton>
                )
              }
              sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <StatusIcon status={item.status} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" noWrap>
                    {item.name}
                  </Typography>
                }
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(item.size)}
                    </Typography>
                    {item.status === 'uploading' && (
                      <LinearProgress
                        variant="determinate"
                        value={item.progress}
                        sx={{ flex: 1, height: 4, borderRadius: 2 }}
                      />
                    )}
                    {item.status === 'error' && (
                      <Chip label={item.error} size="small" color="error" variant="outlined" sx={{ height: 20 }} />
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
        {errorCount > 0 && (
          <Box sx={{ px: 2, py: 1, bgcolor: 'error.50' }}>
            <Typography variant="caption" color="error.main">
              {errorCount} file con errore
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}
